import React, { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { Modal } from 'design-system-web';

import { useAssignPermissionsWizard } from './useAssignPermissionsWizard';
import { TeamsSelectionStep } from './steps/TeamsSelectionStep';
import { PermissionSelectionStep } from './steps/PermissionSelectionStep';
import { ReviewConfirmStep } from './steps/ReviewConfirmStep';
import type { SubmitOutcome } from './PermissionsAssignmentWizardEngine';

type Props = {
  onComplete: (outcome: SubmitOutcome, channelCount: number) => void;
};

/**
 * Renders only when the landing page has activated the permissions-assignment
 * wizard. Reports completion (including partial success) once, via
 * `onComplete`.
 */
export function PermissionsAssignmentWizardModal({ onComplete }: Props) {
  const { t } = useTranslation();
  const wizard = useAssignPermissionsWizard();
  const hasReportedRef = useRef(false);

  useEffect(() => {
    if (wizard.submitOutcome && !hasReportedRef.current) {
      hasReportedRef.current = true;
      onComplete(wizard.submitOutcome, wizard.channelCount);
    }

    if (!wizard.isOpen) {
      hasReportedRef.current = false;
    }
  }, [wizard.submitOutcome, wizard.isOpen, wizard.channelCount, onComplete]);

  if (!wizard.isOpen) {
    return null;
  }

  const permissionLabels = wizard.permissionCategories
    .flatMap(
      (category: { value: { key: string; description: string }[] }) =>
        category.value
    )
    .filter((detail: { key: string }) =>
      wizard.selectedPermissions.includes(detail.key)
    )
    .map((detail: { description: string }) => t(detail.description));

  return (
    <Modal
      isOpen
      size="large"
      onClose={wizard.close}
      title={t(
        'web.admin.portalManagement.bulkTeamManagement.assignPermission.wizard.title'
      )}
      testId="permissionsAssignmentWizardModal"
    >
      {wizard.step === 'teams' && (
        <TeamsSelectionStep
          teamRows={wizard.teamRows}
          teamsTotal={wizard.teamsTotal}
          teamsLoading={wizard.teamsLoading}
          teamsError={wizard.teamsError}
          teamsKeyword={wizard.teamsKeyword}
          teamsPage={wizard.teamsPage}
          teamsPageSize={wizard.teamsPageSize}
          selectedTeamNames={wizard.selectedTeamNames}
          channelCount={wizard.channelCount}
          canAdvance={wizard.canAdvance}
          onTeamsKeywordChange={wizard.onTeamsKeywordChange}
          onTeamsPageChange={wizard.onTeamsPageChange}
          onTeamsPageSizeChange={wizard.onTeamsPageSizeChange}
          onSelect={wizard.selectTeamNames}
          onNext={wizard.next}
          onClose={wizard.close}
        />
      )}
      {wizard.step === 'permissions' && (
        <PermissionSelectionStep
          permissionCategories={wizard.permissionCategories}
          selectedPermissions={wizard.selectedPermissions}
          teamCount={wizard.teamCount}
          canAdvance={wizard.canAdvance}
          onSelect={wizard.selectPermissions}
          onNext={wizard.next}
          onBack={wizard.back}
          onClose={wizard.close}
        />
      )}
      {wizard.step === 'review' && (
        <ReviewConfirmStep
          teamCount={wizard.teamCount}
          channelCount={wizard.channelCount}
          selectedPermissionLabels={permissionLabels}
          isSubmitting={wizard.isSubmitting}
          submitError={wizard.submitError}
          onConfirm={wizard.confirm}
          onBack={wizard.back}
          onClose={wizard.close}
        />
      )}
    </Modal>
  );
}
